import React, { useState, useRef, useEffect, useCallback } from 'react';
import { screenShareAPI } from '@/lib/api';
import { ScreenSharePeer } from '@/lib/screenShare';
import { BrutalButton } from '@/components/brutal';
import { Monitor, X, Loader2, Shield, MousePointer, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_LABELS = {
  idle: 'Enter the code from your admin',
  connecting: 'Connecting...',
  waiting: 'Connected — waiting for admin to join...',
  'peer-joined': 'Admin joined! Starting screen share...',
  sharing: 'Sharing your screen...',
  connected: 'Admin is viewing and controlling your screen',
  'share-denied': 'Screen sharing was denied',
  'share-stopped': 'Screen sharing stopped',
  'peer-left': 'Admin disconnected',
  disconnected: 'Disconnected',
  error: 'Connection error',
};

/**
 * ScreenShareClient — shown to users (guardians/teachers/students).
 * They enter a 6-char code, share their screen, and the admin can
 * view + remotely click/type in their browser tab.
 */
const ScreenShareClient = ({ onClose, initialCode, autoJoin }) => {
  const [code, setCode] = useState((initialCode || '').toUpperCase().slice(0, 6));
  const [status, setStatus] = useState('idle');
  const [sessionId, setSessionId] = useState(null);
  const [adminCursor, setAdminCursor] = useState(null);
  const peerRef = useRef(null);
  const autoJoinedRef = useRef(false);

  // ── Dispatch control events from admin into the DOM ──────────────
  const handleControlEvent = useCallback((event) => {
    if (!event) return;
    const { type, x, y, button, key, code: keyCode, ctrlKey, shiftKey, altKey, metaKey, deltaX, deltaY } = event;

    // Convert relative coords (0-1) to page coords
    const pageX = x * window.innerWidth;
    const pageY = y * window.innerHeight;

    switch (type) {
      case 'click': {
        const el = document.elementFromPoint(pageX, pageY);
        if (el) {
          el.click();
          // Visual feedback
          flashClick(pageX, pageY);
        }
        break;
      }
      case 'dblclick': {
        const el = document.elementFromPoint(pageX, pageY);
        if (el) {
          el.dispatchEvent(new MouseEvent('dblclick', { bubbles: true, clientX: pageX, clientY: pageY }));
          flashClick(pageX, pageY);
        }
        break;
      }
      case 'mousedown': {
        const el = document.elementFromPoint(pageX, pageY);
        if (el) {
          // Focus inputs/textareas
          if (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT') {
            el.focus();
          }
          el.dispatchEvent(new MouseEvent('mousedown', { bubbles: true, clientX: pageX, clientY: pageY, button }));
        }
        break;
      }
      case 'mouseup': {
        const el = document.elementFromPoint(pageX, pageY);
        if (el) {
          el.dispatchEvent(new MouseEvent('mouseup', { bubbles: true, clientX: pageX, clientY: pageY, button }));
        }
        break;
      }
      case 'contextmenu': {
        // Right-click — just ignore to avoid browser menu
        break;
      }
      case 'keydown': {
        const activeEl = document.activeElement || document.body;
        // For input elements, directly manipulate value for printable chars
        if ((activeEl.tagName === 'INPUT' || activeEl.tagName === 'TEXTAREA') && key.length === 1) {
          const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
            activeEl.tagName === 'INPUT' ? window.HTMLInputElement.prototype : window.HTMLTextAreaElement.prototype,
            'value'
          ).set;
          const start = activeEl.selectionStart;
          const end = activeEl.selectionEnd;
          const currentVal = activeEl.value;
          const newVal = currentVal.substring(0, start) + key + currentVal.substring(end);
          nativeInputValueSetter.call(activeEl, newVal);
          activeEl.selectionStart = activeEl.selectionEnd = start + 1;
          activeEl.dispatchEvent(new Event('input', { bubbles: true }));
          activeEl.dispatchEvent(new Event('change', { bubbles: true }));
        } else if (key === 'Backspace' && (activeEl.tagName === 'INPUT' || activeEl.tagName === 'TEXTAREA')) {
          const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
            activeEl.tagName === 'INPUT' ? window.HTMLInputElement.prototype : window.HTMLTextAreaElement.prototype,
            'value'
          ).set;
          const start = activeEl.selectionStart;
          const end = activeEl.selectionEnd;
          const currentVal = activeEl.value;
          let newVal;
          if (start !== end) {
            newVal = currentVal.substring(0, start) + currentVal.substring(end);
          } else if (start > 0) {
            newVal = currentVal.substring(0, start - 1) + currentVal.substring(start);
          } else {
            break;
          }
          nativeInputValueSetter.call(activeEl, newVal);
          activeEl.selectionStart = activeEl.selectionEnd = Math.max(0, start === end ? start - 1 : start);
          activeEl.dispatchEvent(new Event('input', { bubbles: true }));
          activeEl.dispatchEvent(new Event('change', { bubbles: true }));
        } else if (key === 'Tab') {
          // Move focus
          const focusable = Array.from(document.querySelectorAll(
            'input, textarea, select, button, a[href], [tabindex]:not([tabindex="-1"])'
          )).filter(el => !el.disabled && el.offsetParent !== null);
          const idx = focusable.indexOf(activeEl);
          if (idx >= 0) {
            const next = shiftKey ? focusable[idx - 1] : focusable[idx + 1];
            if (next) next.focus();
          }
        } else if (key === 'Enter') {
          activeEl.dispatchEvent(new KeyboardEvent('keydown', { key: 'Enter', code: 'Enter', bubbles: true }));
          if (activeEl.tagName === 'BUTTON' || (activeEl.tagName === 'INPUT' && activeEl.type === 'submit')) {
            activeEl.click();
          }
          if (activeEl.form && activeEl.tagName === 'INPUT') {
            activeEl.form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
          }
        } else {
          // Dispatch keyboard event for non-printable keys
          activeEl.dispatchEvent(new KeyboardEvent('keydown', {
            key, code: keyCode, ctrlKey, shiftKey, altKey, metaKey, bubbles: true,
          }));
        }
        break;
      }
      case 'scroll': {
        const el = document.elementFromPoint(pageX, pageY);
        if (el) {
          // Find scrollable parent
          let scrollTarget = el;
          while (scrollTarget && scrollTarget !== document.body) {
            if (scrollTarget.scrollHeight > scrollTarget.clientHeight || scrollTarget.scrollWidth > scrollTarget.clientWidth) {
              break;
            }
            scrollTarget = scrollTarget.parentElement;
          }
          (scrollTarget || window).scrollBy({ left: deltaX, top: deltaY, behavior: 'auto' });
        }
        break;
      }
      default:
        break;
    }
  }, []);

  // ── Visual click feedback ──────────────────────────────────────────
  const flashClick = (px, py) => {
    const dot = document.createElement('div');
    dot.style.cssText = `
      position:fixed; left:${px - 12}px; top:${py - 12}px; width:24px; height:24px;
      border-radius:50%; background:rgba(99,102,241,0.5); border:2px solid #6366F1;
      pointer-events:none; z-index:999999; transition:all 0.4s ease-out;
    `;
    document.body.appendChild(dot);
    requestAnimationFrame(() => {
      dot.style.transform = 'scale(2)';
      dot.style.opacity = '0';
    });
    setTimeout(() => dot.remove(), 500);
  };

  // ── Admin cursor overlay ───────────────────────────────────────────
  const handleCursorMove = useCallback((msg) => {
    setAdminCursor({ x: msg.x * window.innerWidth, y: msg.y * window.innerHeight });
  }, []);

  // ── Join session ──────────────────────────────────────────────────
  const joinSession = async () => {
    if (code.length < 4) { toast.error('Enter the session code'); return; }
    setStatus('connecting');
    try {
      const res = await screenShareAPI.joinByCode(code.toUpperCase());
      const { session_id } = res.data;
      setSessionId(session_id);

      const peer = new ScreenSharePeer({
        sessionId: session_id,
        role: 'user',
        joinCode: code.toUpperCase(),
        onControlEvent: handleControlEvent,
        onCursorMove: handleCursorMove,
        onStatus: (s) => {
          setStatus(s);
          // Auto-start screen share when admin is waiting
          if (s === 'waiting' || s === 'peer-joined') {
            startSharing(peer);
          }
        },
      });
      peerRef.current = peer;
      await peer.connect();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid code or session expired');
      setStatus('idle');
    }
  };

  const startSharing = async (peer) => {
    const p = peer || peerRef.current;
    if (!p) return;
    try {
      await p.startScreenShare();
    } catch {
      // User cancelled — that's fine
    }
  };

  const stopSharing = () => {
    if (peerRef.current) {
      peerRef.current.disconnect();
      peerRef.current = null;
    }
    setStatus('idle');
    setSessionId(null);
    setAdminCursor(null);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (peerRef.current) peerRef.current.disconnect();
    };
  }, []);

  // Auto-join when launched from a /share/:code link.
  useEffect(() => {
    if (!autoJoin || autoJoinedRef.current) return;
    if ((initialCode || '').length < 4) return;
    autoJoinedRef.current = true;
    joinSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoJoin, initialCode]);

  const isActive = status !== 'idle' && status !== 'disconnected' && status !== 'share-denied' && status !== 'error';
  const isConnected = status === 'connected';

  return (
    <div className="space-y-3">
      {/* Status */}
      <div className="flex items-center gap-2">
        <div className={`w-2.5 h-2.5 rounded-full ${isConnected ? 'bg-green-400 animate-pulse' : isActive ? 'bg-yellow-400' : 'bg-gray-400'}`} />
        <span className="text-xs font-bold" style={{ color: '#F8F5EE' }}>
          {STATUS_LABELS[status] || status}
        </span>
      </div>

      {/* Code entry */}
      {!isActive ? (
        <div className="space-y-2">
          <input
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6))}
            placeholder="Enter code (e.g. A3X9K2)"
            maxLength={6}
            className="w-full px-3 py-2.5 rounded-lg text-center text-lg font-black tracking-[0.2em] bg-white text-gray-900 border-2 border-gray-200 uppercase"
            onKeyDown={(e) => e.key === 'Enter' && joinSession()}
            data-testid="screen-share-code-input"
          />
          <button
            onClick={joinSession}
            disabled={code.length < 4}
            className="w-full py-2.5 rounded-lg font-bold text-sm flex items-center justify-center gap-2 transition-all hover:scale-[1.02] disabled:opacity-50"
            style={{ background: '#6366F1', color: 'white' }}
            data-testid="join-screen-share-btn"
          >
            <Monitor size={16} /> Share My Screen
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          {/* Permission notice */}
          <div className="flex items-start gap-2 p-2 rounded-lg" style={{ background: 'rgba(99,102,241,0.1)' }}>
            <Shield size={14} className="flex-shrink-0 mt-0.5" style={{ color: '#6366F1' }} />
            <p className="text-xs" style={{ color: '#CBD5E1' }}>
              {isConnected
                ? 'Admin can see your screen and interact with this browser tab. They can click, type, and navigate to help you.'
                : 'Connecting you with the admin...'}
            </p>
          </div>

          <button
            onClick={stopSharing}
            className="w-full py-2.5 rounded-lg font-bold text-sm flex items-center justify-center gap-2 transition-all hover:scale-[1.02]"
            style={{ background: '#EF4444', color: 'white' }}
            data-testid="stop-screen-share-btn"
          >
            <X size={16} /> Stop Sharing
          </button>
        </div>
      )}

      {/* Admin cursor overlay (visible on user's screen) */}
      {isConnected && adminCursor && (
        <div
          style={{
            position: 'fixed',
            left: adminCursor.x - 8,
            top: adminCursor.y - 8,
            width: 16,
            height: 16,
            pointerEvents: 'none',
            zIndex: 999998,
          }}
        >
          <MousePointer size={16} style={{ color: '#6366F1', filter: 'drop-shadow(0 0 3px rgba(99,102,241,0.7))' }} />
        </div>
      )}
    </div>
  );
};

export default ScreenShareClient;
